import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Speech from 'expo-speech';
import { Platform } from 'react-native';

let speaking = false;
let availableVoices = [];
let voicesLoaded = false;

export const TTS_VERBOSITY = {
  NONE: 'none',
  LOCATION_ONLY: 'location_only',
  LOCATION_QTY: 'location_qty',
  LOCATION_SKU_QTY: 'location_sku_qty',
  LOCATION_QTY_TOTAL: 'location_qty_total',
  LOCATION_SKU_QTY_TOTAL: 'location_sku_qty_total'
};

const DEFAULT_VERBOSITY = TTS_VERBOSITY.LOCATION_QTY;

export const SPEAK_ONLY_ON_ITEM_CHANGE = [
  TTS_VERBOSITY.LOCATION_ONLY,
  TTS_VERBOSITY.LOCATION_QTY_TOTAL,
  TTS_VERBOSITY.LOCATION_SKU_QTY_TOTAL
];

// Preferred voice identifiers for different platforms
const PREFERRED_VOICES = {
  ios: [
    'com.apple.ttsbundle.Samantha-compact',
    'com.apple.ttsbundle.Alex-compact',
    'com.apple.speech.synthesis.voice.Alex',
    'com.apple.ttsbundle.siri_female_en-US_compact'
  ],
  android: [
    'en-us-x-sfg#female_1-local',
    'en-us-x-sfg#male_1-local',
    'en-US-language'
  ],
  web: [
    'Google US English',
    'Microsoft David - English (United States)',
    'Microsoft Mark - English (United States)',
    'Alex',
    'Karen'
  ]
};

export const getUserVerbositySetting = async () => {
  try {
    const tenant = await AsyncStorage.getItem('tenant');
    const storageKey = tenant ? `ttsVerbosity_${tenant}` : 'ttsVerbosity';

    const savedVerbosity = await AsyncStorage.getItem(storageKey);
    if (
      savedVerbosity &&
      Object.values(TTS_VERBOSITY).includes(savedVerbosity)
    ) {
      return savedVerbosity;
    }
    return DEFAULT_VERBOSITY;
  } catch (error) {
    console.warn('Error getting verbosity setting:', error);
    return DEFAULT_VERBOSITY;
  }
};

export const saveUserVerbositySetting = async verbosityLevel => {
  try {
    const tenant = await AsyncStorage.getItem('tenant');
    const storageKey = tenant ? `ttsVerbosity_${tenant}` : 'ttsVerbosity';

    await AsyncStorage.setItem(storageKey, verbosityLevel);
    return true;
  } catch (error) {
    console.warn('Error saving verbosity setting:', error);
    return false;
  }
};

// Initialize and get available voices
export const initializeVoices = async () => {
  if (voicesLoaded) {
    return availableVoices;
  }

  try {
    const voices = await Speech.getAvailableVoicesAsync();
    availableVoices = voices;
    voicesLoaded = true;
    console.log(
      'Available voices:',
      voices.map(v => `${v.name} (${v.language})`)
    );

    return voices;
  } catch (error) {
    console.warn('Error loading voices:', error);
    voicesLoaded = true;
    return [];
  }
};

// Get the best available voice for the current platform
export const getBestVoice = async () => {
  if (!voicesLoaded) {
    await initializeVoices();
  }

  if (availableVoices.length === 0) {
    return null;
  }

  // Try to find a preferred voice
  const platform = getPlatform();
  const preferredIds = PREFERRED_VOICES[platform] || PREFERRED_VOICES.web;

  for (const preferredId of preferredIds) {
    const voice = availableVoices.find(
      v =>
        v.identifier === preferredId ||
        v.name === preferredId ||
        v.identifier.includes(preferredId)
    );
    if (voice) {
      console.log(
        `Selected preferred voice: ${voice.name} (${voice.identifier})`
      );
      return voice;
    }
  }

  // Fallback: find any English voice
  const englishVoice = availableVoices.find(
    v =>
      v.language.toLowerCase().startsWith('en') ||
      v.language.toLowerCase().includes('english')
  );

  if (englishVoice) {
    console.log(
      `Selected English voice: ${englishVoice.name} (${englishVoice.identifier})`
    );
    return englishVoice;
  }

  // Ultimate fallback: first available voice
  console.log(`Using first available voice: ${availableVoices[0].name}`);
  return availableVoices[0];
};

const getPlatform = () => {
  if (typeof window === 'undefined') {
    return 'native';
  }

  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'ios';
  }

  if (/android/i.test(userAgent)) {
    return 'android';
  }

  return 'web';
};

export const isIOSDevice = () => {
  if (Platform.OS === 'ios') return true;

  if (typeof window !== 'undefined') {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
  }

  return false;
};

export const isVoiceInitialized = () => {
  return voicesLoaded;
};

// Test TTS functionality
export const testTTS = async () => {
  try {
    const isAvailable = await Speech.isSpeakingAsync();

    if (speaking || isAvailable) {
      await Speech.stop();
    }

    // Quick test with minimal volume
    await Speech.speak('Test', {
      language: 'en-US',
      pitch: 1.0,
      rate: 1.0,
      volume: 0.1
    });

    // Stop immediately after starting
    setTimeout(() => Speech.stop(), 100);

    return {success: true};
  } catch (error) {
    console.error('TTS test failed:', error);
    return {success: false, error: error.message};
  }
};

export const formatSpeechMessage = async (
  item,
  customVerbosity = null,
  isItemChange = true
) => {
  let verbosity = customVerbosity;

  if (!verbosity) {
    verbosity = await getUserVerbositySetting();
  }

  if (verbosity === TTS_VERBOSITY.NONE) {
    return null;
  }

  if (!isItemChange && SPEAK_ONLY_ON_ITEM_CHANGE.includes(verbosity)) {
    return null;
  }

  const location = item.location || '';
  const sku = item.sku || item.name || 'Item';
  const qtyRemaining = item.qty_remaining || 0;
  const qtyTotal = qtyRemaining + (item.scanned_qty || 0) || 1;

  const isKit =
    item.product_type === 'individual' &&
    item.child_items &&
    item.child_items.length > 0;

  let message = '';

  if (isKit) {
    const childItem = item.child_items[0];
    const childLocation = childItem?.location || '';

    switch (verbosity) {
      case TTS_VERBOSITY.LOCATION_ONLY:
        message = childLocation ? `Location ${childLocation}` : '';
        break;
      case TTS_VERBOSITY.LOCATION_QTY:
        message = childLocation
          ? `Location ${childLocation}, ${qtyRemaining} remaining`
          : `${qtyRemaining} remaining`;
        break;
      case TTS_VERBOSITY.LOCATION_SKU_QTY:
        message = childLocation
          ? `${sku}, location ${childLocation}, ${qtyRemaining} remaining`
          : `${sku}, ${qtyRemaining} remaining`;
        break;
      case TTS_VERBOSITY.LOCATION_QTY_TOTAL:
        message = childLocation
          ? `Location ${childLocation}, ${qtyTotal} total`
          : `${qtyTotal} total`;
        break;
      case TTS_VERBOSITY.LOCATION_SKU_QTY_TOTAL:
        message = childLocation
          ? `${sku}, location ${childLocation}, ${qtyTotal} total`
          : `${sku}, ${qtyTotal} total`;
        break;
    }

    if (message) {
      message = `Kit ${message}`;
    }
  } else {
    switch (verbosity) {
      case TTS_VERBOSITY.LOCATION_ONLY:
        message = location ? `Location ${location}` : '';
        break;
      case TTS_VERBOSITY.LOCATION_QTY:
        message = location
          ? `Location ${location}, ${qtyRemaining} remaining`
          : `${qtyRemaining} remaining`;
        break;
      case TTS_VERBOSITY.LOCATION_SKU_QTY:
        message = location
          ? `${sku}, location ${location}, ${qtyRemaining} remaining`
          : `${sku}, ${qtyRemaining} remaining`;
        break;
      case TTS_VERBOSITY.LOCATION_QTY_TOTAL:
        message = location
          ? `Location ${location}, ${qtyTotal} total`
          : `${qtyTotal} total`;
        break;
      case TTS_VERBOSITY.LOCATION_SKU_QTY_TOTAL:
        message = location
          ? `${sku}, location ${location}, ${qtyTotal} total`
          : `${sku}, ${qtyTotal} total`;
        break;
    }
  }

  return message;
};

export const isTTSInProgress = async () => {
  try {
    return speaking || (await Speech.isSpeakingAsync());
  } catch (error) {
    return speaking;
  }
};

export const stopSpeaking = async () => {
  try {
    await Speech.stop();
    speaking = false;

    // Remove speech indicator if present
    const indicator = document.getElementById('speech-indicator');
    if (indicator) {
      document.body.removeChild(indicator);
    }
  } catch (error) {
    console.warn('Error stopping speech:', error);
    speaking = false;
  }
};

export const speakText = async (text, options = {}) => {
  try {
    if (!text) {
      console.warn('No text provided to speak');
      return {success: false, error: 'No text provided'};
    }

    // Stop any current speech
    await stopSpeaking();

    speaking = true;

    // Create speech indicator (for web platforms)
    let speechIndicator = null;
    if (typeof document !== 'undefined') {
      speechIndicator = document.createElement('div');
      speechIndicator.id = 'speech-indicator';
      speechIndicator.style.position = 'fixed';
      speechIndicator.style.top = '20px';
      speechIndicator.style.right = '20px';
      speechIndicator.style.padding = '10px 20px';
      speechIndicator.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
      speechIndicator.style.color = 'white';
      speechIndicator.style.borderRadius = '5px';
      speechIndicator.style.zIndex = '9999';
      speechIndicator.style.fontFamily = 'Arial, sans-serif';
      speechIndicator.style.fontSize = '16px';
      speechIndicator.textContent = `🔊 Speaking: "${text}"`;

      document.body.appendChild(speechIndicator);
    }

    // Get best voice
    const voice = await getBestVoice();

    // Prepare speech options
    const speechOptions = {
      language: options.language || voice?.language || 'en-US',
      pitch: options.pitch || 1.0,
      rate: options.rate || 0.9,
      volume: options.volume || 1.0,
      voice: voice?.identifier,
      ...options
    };

    console.log('Speaking with options:', speechOptions);

    return new Promise(resolve => {
      let resolved = false;

      const cleanup = () => {
        if (resolved) return;
        resolved = true;

        speaking = false;
        if (speechIndicator && speechIndicator.parentNode) {
          speechIndicator.parentNode.removeChild(speechIndicator);
        }
      };

      // Set up speech callbacks
      speechOptions.onStart = () => {
        console.log('Speech started');
      };

      speechOptions.onDone = () => {
        cleanup();
        resolve({success: true});
      };

      speechOptions.onStopped = () => {
        cleanup();
        resolve({success: true, stopped: true});
      };

      speechOptions.onError = error => {
        console.error('Speech error:', error);
        cleanup();
        resolve({success: false, error: error.message || 'Speech error'});
      };

      // Start speaking
      Speech.speak(text, speechOptions);

      // Backup timeout
      setTimeout(() => {
        if (!resolved) {
          console.warn('Speech timeout');
          Speech.stop();
          cleanup();
          resolve({success: false, error: 'Speech timeout'});
        }
      }, 10000); // 30 second timeout
    });
  } catch (error) {
    console.error('speakText error:', error);
    speaking = false;

    const indicator = document.getElementById('speech-indicator');
    if (indicator) {
      document.body.removeChild(indicator);
    }

    return {success: false, error: error.message};
  }
};

// Auto-initialize voices when module loads
initializeVoices();
